import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { taskAPI } from '../../services/api';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' }
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' }
];

const PRIORITY_CLASS = {
  HIGH: 'priority-high',
  MEDIUM: 'priority-medium',
  LOW: 'priority-low'
};

const TaskList = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolvedBy, setResolvedBy] = useState('');

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (priorityFilter) filters.priority = priorityFilter;
      const result = await taskAPI.getAll(filters);
      setTasks(result.tasks || []);
    } catch (err) {
      setError('Failed to load tasks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [statusFilter, priorityFilter]);

  const handleAssign = async () => {
    if (!assignTo.trim() || !selectedTask) return;
    try {
      await taskAPI.assign(selectedTask.id, assignTo.trim());
      setShowAssignModal(false);
      setAssignTo('');
      setSelectedTask(null);
      fetchTasks();
    } catch (err) {
      console.error('Failed to assign task:', err);
    }
  };

  const handleResolve = async () => {
    if (!resolvedBy.trim() || !selectedTask) return;
    try {
      await taskAPI.resolve(selectedTask.id, resolvedBy.trim(), resolveNotes.trim());
      setShowResolveModal(false);
      setResolveNotes('');
      setResolvedBy('');
      setSelectedTask(null);
      fetchTasks();
    } catch (err) {
      console.error('Failed to resolve task:', err);
    }
  };

  const isOverdue = (task) => {
    if (task.status === 'RESOLVED') return false;
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) return <div className="loading">Loading tasks...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="task-list-page">
      <div className="page-header">
        <h1>Operator Tasks</h1>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="status-tabs">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`status-tab ${statusFilter === opt.value ? 'active' : ''}`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <select
          className="filter-select"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          {PRIORITY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {tasks.length === 0 ? (
        <div className="empty-state">
          <p>No tasks found</p>
        </div>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Assigned To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map(task => (
              <tr key={task.id} className={isOverdue(task) ? 'task-overdue' : ''}>
                <td>
                  <div className="task-title">{task.title}</div>
                  {task.shipmentId && (
                    <Link to={`/shipments/${task.shipmentId}`} className="text-small text-muted">
                      Shipment: {task.shipmentId.slice(0, 8)}...
                    </Link>
                  )}
                </td>
                <td>
                  <span className="badge badge-tag">
                    {task.taskType.replace(/_/g, ' ')}
                  </span>
                </td>
                <td>
                  <span className={`badge ${PRIORITY_CLASS[task.priority] || ''}`}>
                    {task.priority}
                  </span>
                </td>
                <td>
                  <span className={`badge badge-task-${task.status.toLowerCase().replace('_', '-')}`}>
                    {task.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className={isOverdue(task) ? 'overdue-date' : ''}>
                  {formatDate(task.dueDate)}
                  {isOverdue(task) && <span className="overdue-label">OVERDUE</span>}
                </td>
                <td>{task.assignedTo || '-'}</td>
                <td className="actions">
                  {task.status !== 'RESOLVED' && (
                    <>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => { setSelectedTask(task); setShowAssignModal(true); }}
                      >
                        Assign
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => { setSelectedTask(task); setShowResolveModal(true); }}
                      >
                        Resolve
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Task</h2>
              <button className="btn-close" onClick={() => setShowAssignModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="text-small" style={{ marginBottom: '1rem' }}>{selectedTask?.title}</p>
              <div className="form-group">
                <label>Assign To</label>
                <input
                  type="text"
                  value={assignTo}
                  onChange={e => setAssignTo(e.target.value)}
                  placeholder="Enter operator name"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAssign} disabled={!assignTo.trim()}>
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="modal-overlay" onClick={() => setShowResolveModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Resolve Task</h2>
              <button className="btn-close" onClick={() => setShowResolveModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="text-small" style={{ marginBottom: '1rem' }}>{selectedTask?.title}</p>
              <div className="form-group">
                <label>Resolved By</label>
                <input
                  type="text"
                  value={resolvedBy}
                  onChange={e => setResolvedBy(e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div className="form-group">
                <label>Resolution Notes</label>
                <textarea
                  value={resolveNotes}
                  onChange={e => setResolveNotes(e.target.value)}
                  placeholder="Describe how the task was resolved"
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowResolveModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleResolve} disabled={!resolvedBy.trim()}>
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;
